/** Admin credential management page (FR-CRD-01 through FR-CRD-06). */

import { useState } from "react";
import {
  Box, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataTable, useTableCollection, type Column, PageHeader } from "@selerity/ui";
import { useAdminCredentials, useCreateCredential, useDeleteCredential } from "@/hooks/useAdmin";

const MULTILINE_SECRETS = new Set(["service_account_json"]);

const CRED_FIELDS: Record<string, { fields: string[]; secrets: string[] }> = {
  accelo: { fields: ["deployment"], secrets: ["client_id", "client_secret"] },
  zendesk: { fields: ["subdomain", "email"], secrets: ["api_token"] },
  datadog: { fields: ["site"], secrets: ["api_key", "application_key"] },
  google_drive: { fields: ["default_folder_id", "admin_email"], secrets: ["service_account_json"] },
  google_sheets: { fields: ["admin_email"], secrets: ["service_account_json"] },
  google_gemini: { fields: [], secrets: ["api_key"] },
};

interface Credential {
  credentialId: string;
  name: string;
  credentialType: string;
  createdAt: string;
}

export default function Credentials() {
  const { data, isLoading, isError, error } = useAdminCredentials();
  const createCred = useCreateCredential();
  const deleteCred = useDeleteCredential();
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({ name: "", credentialType: "accelo" });

  const typeDef = CRED_FIELDS[form.credentialType as keyof typeof CRED_FIELDS] ?? { fields: [], secrets: [] };
  const credentials: Credential[] = data?.items ?? [];

  const columns: Column<Credential>[] = [
    { id: "name", header: "Name", sortField: "name", cell: (c) => c.name, filterType: "text", filterValue: (c) => c.name },
    { id: "type", header: "Type", sortField: "credentialType", cell: (c) => c.credentialType },
    { id: "created", header: "Created", sortField: "createdAt", cell: (c) => new Date(c.createdAt).toLocaleDateString() },
    {
      id: "actions", header: "", width: 60,
      cell: (c) => (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteCred.mutate(c.credentialId); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const table = useTableCollection({ items: credentials, defaultSortField: "name" });

  const handleCreate = () => {
    const fields: Record<string, string> = {};
    const secrets: Record<string, string> = {};
    for (const f of (typeDef?.fields ?? [])) fields[f] = form[f] ?? "";
    for (const s of (typeDef?.secrets ?? [])) secrets[s] = form[s] ?? "";
    createCred.mutate({ name: form.name, credentialType: form.credentialType, fields, secrets }, {
      onSuccess: () => { setDialog(false); setForm({ name: "", credentialType: "accelo" }); },
    });
  };

  return (
    <Box>
      <PageHeader
        title="Credentials"
        counter={`(${credentials.length})`}
        actions={<Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog(true)}>New Credential</Button>}
      />

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load credentials: {error.message}</Alert>}
      {createCred.isError && <Alert severity="error" sx={{ mb: 2 }}>{createCred.error.message}</Alert>}
      {deleteCred.isError && <Alert severity="error" sx={{ mb: 2 }}>{deleteCred.error.message}</Alert>}

      <DataTable
        columns={columns}
        items={table.paginatedItems}
        totalCount={table.totalCount}
        loading={isLoading}
        sortField={table.sortField}
        sortDescending={table.sortDescending}
        onSort={table.onSort}
        page={table.page}
        pageSize={table.pageSize}
        onPageChange={table.onPageChange}
        onPageSizeChange={table.onPageSizeChange}
        rowKey={(c) => c.credentialId}
        emptyText="No credentials yet."
      />

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Credential</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField label="Name" value={form.name ?? ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} size="small" />
          <TextField label="Type" select value={form.credentialType} onChange={(e) => setForm({ name: form.name ?? "", credentialType: e.target.value })} size="small" SelectProps={{ native: true }}>
            {Object.keys(CRED_FIELDS).map((t) => <option key={t} value={t}>{t}</option>)}
          </TextField>
          {(typeDef?.fields ?? []).map((f: string) => (
            <TextField key={f} label={f} value={form[f] ?? ""} onChange={(e) => setForm((prev) => ({ ...prev, [f]: e.target.value }))} size="small" />
          ))}
          {(typeDef?.secrets ?? []).map((s: string) =>
            MULTILINE_SECRETS.has(s) ? (
              <TextField key={s} label={s} multiline rows={6} value={form[s] ?? ""} onChange={(e) => setForm((prev) => ({ ...prev, [s]: e.target.value }))} size="small" inputProps={{ style: { fontFamily: "monospace", fontSize: "0.8rem" } }} />
            ) : (
              <TextField key={s} label={s} type="password" value={form[s] ?? ""} onChange={(e) => setForm((prev) => ({ ...prev, [s]: e.target.value }))} size="small" />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
